import React from "react";
import styled from "styled-components";
import { colors, typography, spacing } from "../styles/designSystem";
import {
    Panel,
    PanelLabel,
    Caption,
    PipelineFlow,
    StepGrid,
} from "./diagrams/primitives";

/**
 * Diagrams for the Spectrum WIN case study.
 *
 * Three views of the same product at different altitudes: the agent's path through
 * a call, the architecture underneath it, and the contract a single component
 * satisfies. Built as DOM rather than images so they retheme with the phosphor and
 * stay readable when a viewer zooms.
 */



// ===== 1. Agent workflow — the path through a single call =====




const AGENT_STEPS = [
    { name: "Incoming Call", color: colors.coral[400] },
    { name: "Customer Context", color: colors.amber[400] },
    { name: "Issue Detection", color: colors.primary[500] },
    { name: "Troubleshooting", color: colors.cyan[300] },
    { name: "Resolution", color: colors.emerald[500] },
];

export const AgentWorkflow = () => (
    <StepGrid
        label="Agent Workflow — One Call"
        steps={AGENT_STEPS}
        columns={5}
    />
);

// ===== 2. Application architecture — the layers under that path =====




const ARCH_LAYERS = [
    { name: "Customer Data", desc: "Account, service status, equipment, interaction history", color: colors.amber[400] },
    { name: "State / Logic", desc: "What this customer and this scenario actually require", color: colors.cyan[300] },
    { name: "Component System", desc: "Reusable patterns with defined states and behavior", color: colors.primary[500] },
    { name: "Agent UI", desc: "The surface an agent works from, without leaving it", color: colors.emerald[500] },
];

export const AppArchitecture = () => (
    <PipelineFlow label="Application Architecture" stages={ARCH_LAYERS} />
);

// ===== 3. Component anatomy — the contract one component satisfies =====
const Chain = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[150]};
    align-items: center;
`;

const Node = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[100]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.normal};
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => theme.borderMedium};
    padding: ${spacing[300]} ${spacing[300]};
    white-space: nowrap;
`;

const Chevron = styled.span`
    color: ${({ theme }) => theme.primary};
    opacity: 0.6;
`;


const ANATOMY = ["Props / Data", "State", "Variant", "Interaction", "Accessibility", "Render"];

export const ComponentAnatomy = () => (
    <Panel>
        <PanelLabel>Component Anatomy</PanelLabel>
        <Chain>
            {ANATOMY.map((node, i) => (
                <React.Fragment key={node}>
                    <Node>{node}</Node>
                    {i < ANATOMY.length - 1 && <Chevron aria-hidden="true">&#8250;</Chevron>}
                </React.Fragment>
            ))}
        </Chain>
        <Caption>
            Every reusable pattern satisfies the same contract. Accessibility sits inside it, not
            after it &mdash; which is why fixing focus or labelling once fixed it everywhere.
        </Caption>
    </Panel>
);

// ===== State coverage — the states a workflow has to survive =====
const States = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[200]};
`;

const StateChip = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    border: 1px solid ${({ color }) => color};
    padding: ${spacing[200]} ${spacing[400]};
`;

const APP_STATES = [
    { name: "Loading", color: colors.cyan[300] },
    { name: "Data Available", color: colors.emerald[500] },
    { name: "Partial Data", color: colors.amber[400] },
    { name: "Empty", color: colors.gray[300] },
    { name: "Error", color: colors.coral[400] },
    { name: "Recovery", color: colors.primary[500] },
];

export const StateCoverage = () => (
    <Panel>
        <PanelLabel>States Every Workflow Handles</PanelLabel>
        <States>
            {APP_STATES.map((state) => (
                <StateChip key={state.name} color={state.color}>
                    {state.name}
                </StateChip>
            ))}
        </States>
        <Caption>
            Mockups show the second one. Production spends real time in all six, so components were
            built against the set rather than against the screenshot.
        </Caption>
    </Panel>
);
